import React, { useEffect, useState } from 'react';
import { emptyArray } from '../../constants/collections';
import Image from './Image';
import Gallery from './Gallery';
import { isAsyncFn } from '../../utils/isAsyncFn';
import { useEvents } from '../../providers/events';
import { base64ToUint8Array } from '../../../../helpers/attachments/base64ToUint8Array';
import ImagesSkeleton from './ImagesSkeleton';
import styles from './index.module.scss';

const Images = ({ data = emptyArray, removeFile, commentId }) => {
  const [showPreview, setShowPreview] = useState(false);

  const { getCommentAttachment } = useEvents();

  const [index, setIndex] = useState(0);
  const [urlsData, setUrlsData] = useState([]);
  const [loading, setLoading] = useState(false);

  const galleryHandlers = createGalleryHandlers(data.length, setIndex);

  useEffect(() => {
    let cancelled = false;
    const result = [];

    const loadFile = async (file, index) => {
      if (file instanceof File) {
        result.push({ alt: file.name, url: URL.createObjectURL(file), index });
        return;
      }

      if (commentId) {
        const { fileName } = file;

        if (isAsyncFn(getCommentAttachment)) {
          const data = await getCommentAttachment({ fileName, commentId });

          if (!cancelled && data !== 'Error') {
            const uint8 = base64ToUint8Array(data);
            const blob = new Blob([uint8]);
            result.push({
              alt: file.name,
              url: URL.createObjectURL(blob),
              index,
            });
          }

          return;
        }

        const data = getCommentAttachment({ fileName, commentId });
        const uint8 = base64ToUint8Array(data);
        const blob = new Blob([uint8]);
        result.push({ alt: file.name, url: URL.createObjectURL(blob), index });
      }
    };

    setLoading(true);

    Promise.allSettled(data.map(loadFile)).then(() => {
      if (!cancelled) {
        setUrlsData(result);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      result.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [data, commentId, getCommentAttachment]);

  const handleImageClick = (index) => {
    setShowPreview(true);
    setIndex(index);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  return (
    <>
      <div className={styles.images}>
        {loading ? (
          <ImagesSkeleton size={data.length} />
        ) : (
          urlsData.map(({ alt, url, index }) => (
            <Image
              key={url}
              src={url}
              name={alt}
              removeFile={removeFile}
              index={index}
              onClick={handleImageClick}
            />
          ))
        )}
      </div>
      {showPreview && (
        <Gallery
          {...galleryHandlers}
          onClose={handleClosePreview}
          preview={urlsData[index].url}
          name={urlsData[index].alt}
        />
      )}
    </>
  );
};

export default Images;

function createGalleryHandlers(size, setIndex) {
  if (size <= 1) {
    return {
      onNext: undefined,
      onPrev: undefined,
      onKeyDown: undefined,
    };
  }

  const onNext = () => {
    setIndex((prevIndex) => {
      const next = prevIndex + 1;
      return next < size ? next : 0;
    });
  };

  const onPrev = () => {
    setIndex((prevIndex) => {
      const next = prevIndex - 1;
      return next >= 0 ? next : size - 1;
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      onNext();
    }
    if (e.key === 'ArrowLeft') {
      onPrev();
    }
  };

  return { onNext, onPrev, onKeyDown };
}
